import { useCallback, useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { borderColorForBg } from "@/lib/constants";

export interface SegmentedTab<K extends string = string> {
  key: K;
  label: string;
  /** Optional trailing count chip. Omit for tabs that have nothing to count. */
  count?: number;
  /**
   * The group's own colour (hex), worn by the ring the pill emits as it lands
   * on this tab. The pill's own colour is always the brand blue — a standing
   * red pill made "Needs you" shout all day; the arrival ring says the same
   * thing once.
   */
  tone?: string;
}

interface SegmentedTabsProps<K extends string> {
  tabs: ReadonlyArray<SegmentedTab<K>>;
  value: K;
  /**
   * `NoInfer` because a `useState` setter passed here contributes
   * `SetStateAction<K>` as an inference candidate, which fails `K extends
   * string` and collapses K to plain `string`. The tab list is the source of
   * truth for the key type.
   */
  onChange: (key: NoInfer<K>) => void;
  className?: string;
  /** Names the group for screen readers, since the visible label is the card's own heading. */
  ariaLabel?: string;
}

/** How long the ring pulse owns the pill's shadow before the ring returns. */
const PULSE_MS = 450;

/**
 * One rounded segmented control: a single track holding equal-width segments,
 * with one highlight pill that *slides* between them.
 *
 * Why a sliding pill rather than a per-button background: swapping which of
 * three loose pills carries `bg-secondary` repaints two elements at once, which
 * reads as a flicker. Moving one element with `transform` reads as the control
 * responding — the same glide the nav uses. Equal-width segments make the
 * travel exactly one segment per index, so the transform is a plain
 * `translateX(index * 100%)` with no measuring.
 *
 * Colour note: the track is `--background`, not `--muted`/`--secondary`.
 * `--muted` is identical to `--background` in light mode and sits one lightness
 * step off `--card` in dark mode, so a muted track would vanish against the
 * card it lives on. `--background` is the canvas colour, so the track reads as
 * a recessed groove.
 *
 * The pill itself rests on `--primary`, the app's solid button blue (white
 * label at 10:1) — always. On a user-driven tab change it emits ONE outward
 * ring pulse in the target tab's `tone` (`.segmented-arrive`, the hero
 * finish's burst family), fired at click so the control answers instantly.
 * Never on mount: a page that opens with a red pulse says "alarm" when
 * nothing changed. (A colour flash was tried twice and reviewed out.)
 *
 * `.segmented-pill` (index.css) adds the rare idle shine.
 */
const SegmentedTabs = <K extends string>({ tabs, value, onChange, className, ariaLabel }: SegmentedTabsProps<K>) => {
  const activeIndex = Math.max(
    0,
    tabs.findIndex((t) => t.key === value),
  );

  // The pulse: one outward ring in the target tab's tone, fired from the
  // CLICK HANDLER so the control answers instantly — and so clicking the tab
  // the pill is already on still answers (an effect keyed on `value` can't
  // see a same-value click). A colour flash was tried twice and reviewed
  // out; the ring alone is the feedback that survived.
  const [pulse, setPulse] = useState<{ tone: string } | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  const firePulse = useCallback((tone: string | undefined) => {
    if (!tone) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    // Null-then-set across a frame restarts the CSS animation when the class
    // is already applied (a repeat click mid-pulse, or the same tab again).
    setPulse(null);
    requestAnimationFrame(() => {
      setPulse({ tone });
      timerRef.current = setTimeout(() => setPulse(null), PULSE_MS);
    });
  }, []);

  const pulsing = pulse !== null;

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cn("relative grid rounded-full bg-background p-1", className)}
      style={{ gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))` }}
    >
      {/* The one moving part. `inset-y-1 left-1` parks it inside the p-1 track
          inset, so its width is a segment of the *inner* width and a 100% step
          lands it exactly on the next segment. */}
      <span
        aria-hidden
        className={cn(
          "segmented-pill pointer-events-none absolute inset-y-1 left-1 rounded-full bg-primary shadow-sm ring-1 ring-[hsl(var(--primary))] transition-transform motion-reduce:transition-none",
          // The pulse rides the pill's OWN box-shadow (the pill clips its
          // children for the shine sweep, but overflow never clips an
          // element's own shadow). The animation owns box-shadow for its
          // 450ms — the resting ring returns with the class.
          pulsing && "segmented-arrive",
        )}
        style={{
          width: `calc((100% - 0.5rem) / ${tabs.length})`,
          transform: `translateX(${activeIndex * 100}%)`,
          transitionDuration: "260ms",
          transitionTimingFunction: "cubic-bezier(.2,0,0,1)",
          // The pulse wears the tone's darker outline shade (the flag-outline
          // recipe): the raw pastels — To do's pale blue especially — vanish
          // against a light page. ~66% alpha (8-digit hex).
          ...(pulsing ? { ["--pulse-tone" as string]: `${borderColorForBg(pulse.tone)}a8` } : {}),
        }}
      />
      {tabs.map((t) => {
        const active = t.key === value;
        return (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => {
              firePulse(t.tone);
              onChange(t.key);
            }}
            className={cn(
              // z-10 keeps the label above the pill it slides under.
              "press-pop relative z-10 inline-flex min-w-0 items-center justify-center gap-1.5 rounded-full px-2 py-1 text-xs font-bold transition-colors",
              active ? "text-white" : "text-muted-foreground hover:text-foreground",
            )}
          >
            <span className="truncate">{t.label}</span>
            {t.count !== undefined && (
              <span
                className={cn(
                  "shrink-0 rounded-full px-1.5 text-[10px] font-bold leading-4",
                  active
                    ? // On the filled pill the chip can only be a hole punched in
                      // it: a second solid fill on a solid pill is two badges deep.
                      "bg-white/25 text-white"
                    : // A tint of the text colour, not `--muted`: the inactive chip sits
                      // directly on the `--background` track, which `--muted` matches exactly
                      // in light mode.
                      "bg-muted-foreground/20 text-muted-foreground",
                )}
              >
                {t.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

export default SegmentedTabs;
